"use client";

import { Box, Stack, keyframes, Tooltip } from "@mui/material";
import { Mic, SendHorizontal, Square } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useRef, useState, KeyboardEvent, Suspense } from "react";
import { VoiceProvider } from "@/context/voiceContext";
import FullScreenSpeechInput from "../voiceInputModal";
import objectToQueryString from "@/utils";
import { useConversational } from "@/context/ConversationalContext";
import { Message } from "../page";
import { enqueueSnackbar } from "notistack";
import gaService from "@/api/services/ga.service";
import useDevice from "@/hooks/useDevice";

const glowPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 #b6a8ed;
  }
  70% {
    box-shadow: 0 0 0 10px rgba(101, 64, 247, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(101, 64, 247, 0);
  }
`;

const ConversationalInputBox = () => {
  const [voiceOpen, setVoiceOpen] = useState(false);
  const searchParams = useSearchParams();
  const sessionId = searchParams?.get("sid") ?? null;

  const [inputValue, setInputValue] = useState("");
  const [lastSent, setLastSent] = useState<string>("");

  const abortRef = useRef<AbortController | null>(null);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  // ✨ highlight state + timer ref
  const [sendHighlighted, setSendHighlighted] = useState(false);
  const highlightTimeoutRef = useRef<number | null>(null);

  const {
    isLoading,
    setIsStreaming,
    setIsLoading,
    setStreamingMsg,
    loadSessions,
    addLocalMessage,
    currentSessionId,
    isNewChat,
  } = useConversational();

  const router = useRouter();

  useEffect(() => {
    textAreaRef.current?.focus();
  }, [isLoading]);

  const stopStreaming = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setIsStreaming(false);
    setIsLoading(false);
    setStreamingMsg(null);
  }, [setIsStreaming, setIsLoading, setStreamingMsg]);

  async function handleSendMessage(msgRaw?: string) {
    const msg = (msgRaw ?? inputValue).trim();
    if (!msg || isLoading) return;

    const userId = localStorage.getItem("userId") || "";
    const authtoken = localStorage.getItem("trends_user") || "";
    if (!authtoken) {
      localStorage.clear();
      enqueueSnackbar("Your session has expired. Please log in again.", {
        variant: "error",
      });
      return;
    }
    setIsLoading(true);
    setInputValue("");
    setLastSent(msg);

    const humanMessage: any = {
      id: `user-${Date.now()}`,
      role: "user",
      message: msg,
      charts: [],
      static_charts: [],
    };
    addLocalMessage(humanMessage);

    setStreamingMsg(null);
    setIsStreaming(false);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const url =
        process.env.NEXT_PUBLIC_BASE_API_URL +
        "v4/trends/chat" +
        objectToQueryString({
          query: msg,
          ...(sessionId ? { session_id: sessionId } : {}),
        });

      const res = await fetch(url, {
        signal: controller.signal,
        headers: new Headers({
          "Content-Type": "application/json",
          "X-USER-ID-TRENDS": userId,
          Authorization: `Bearer ${authtoken}`,
        }),
      });

      const headerSid = res.headers.get("x-trends-ai-session-id");

      // GA : track user message (conversation_id from sessionId or headerSid)
      gaService.conversationUserMessage(
        msg,
        sessionId ?? headerSid ?? "unknown"
      );

      // Update router if new session id generated
      if (!currentSessionId && headerSid) {
        router.replace(`/ask-ai?sid=${headerSid}`);
      }

      if (!res?.body) {
        setIsStreaming(false);
        setIsLoading(false);
        addLocalMessage({
          id: `assistant-${Date.now()}`,
          role: "assistant",
          message: "No response body (stream) from server.",
          charts: [],
          static_charts: [],
        });
        return;
      }

      setIsStreaming(true);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      let accumulatedText = "";
      let accumulatedCharts: any[] = [];
      let accumulatedStaticCharts: any[] = [];

      const parseLine = (line: string) => {
        const clean = line.replace(/^data:\s*/, "").trim();
        if (!clean) return null;
        if (clean === "[DONE]") return { doneSignal: true } as any;
        try {
          return JSON.parse(clean);
        } catch {
          return null;
        }
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split(/\r?\n/);
        buffer = lines.pop() ?? "";

        for (const raw of lines) {
          const parsed = parseLine(raw);
          if (!parsed) continue;

          if ((parsed as any).doneSignal) {
            buffer = "";
            reader.cancel?.();
            break;
          }
          const textChunk = (parsed as any).insights ?? [];
                const insightsArray = Array.isArray(textChunk)
                ? textChunk
                : typeof textChunk === "string"
                ? [textChunk]
                : [];
                const insights = insightsArray
                .map((item: any) =>
                  typeof item === "string" ? item : JSON.stringify(item)
                )
                .join("\n\n");
          // const insights = (parsed as any).insights ?? "";
          const chartsChunk = Array.isArray((parsed as any).charts)
            ? (parsed as any).charts
            : [];
          const staticChartsChunk = Array.isArray((parsed as any).staticCharts)
            ? (parsed as any).staticCharts
            : [];

          accumulatedText += insights;
          accumulatedCharts = [
            ...accumulatedCharts,
            ...chartsChunk,
            ...staticChartsChunk,
          ];
          accumulatedStaticCharts = [
            ...accumulatedStaticCharts,
            ...staticChartsChunk,
          ];

          setStreamingMsg((prev: any) => {
            const base =
              prev ??
              ({
                id: "streaming-assistant",
                role: "assistant",
                message: "",
                charts: [],
                static_charts: [],
                isStreaming: true,
              } as any);

            return {
              ...base,
              message: base.message + insights,
              charts: [
                ...(base.charts ?? []),
                ...chartsChunk,
                ...staticChartsChunk,
              ],
              static_charts: [
                ...(base.static_charts ?? []),
                ...staticChartsChunk,
              ],
              isStreaming: true,
            } as any;
          });
        }
      }

      if (buffer.trim()) {
        try {
          const tail = JSON.parse(buffer.replace(/^data:\s*/, "").trim());
          const insights = tail.insights ?? "";
          const chartsChunk = Array.isArray(tail.charts) ? tail.charts : [];
          const staticChartsChunk = Array.isArray(tail.staticCharts)
            ? tail.staticCharts
            : [];

          accumulatedText += insights;
          accumulatedCharts = [
            ...accumulatedCharts,
            ...chartsChunk,
            ...staticChartsChunk,
          ];
          accumulatedStaticCharts = [
            ...accumulatedStaticCharts,
            ...staticChartsChunk,
          ];

          setStreamingMsg((prev: any) => {
            const base =
              prev ??
              ({
                id: "streaming-assistant",
                role: "assistant",
                message: "",
                charts: [],
                static_charts: [],
                isStreaming: true,
              } as any);

            return {
              ...base,
              message: base.message + insights,
              charts: [
                ...(base.charts ?? []),
                ...chartsChunk,
                ...staticChartsChunk,
              ],
              static_charts: [
                ...(base.static_charts ?? []),
                ...staticChartsChunk,
              ],
              isStreaming: true,
            } as Message;
          });
        } catch {
          // ignore JSON parse error
        }
      }

      // ✅ IMPORTANT: clear streaming state BEFORE committing final message
      if (accumulatedText.trim()) {
        // 1. stop streaming so UI stops merging streamingMsg
        setStreamingMsg(null);
        setIsStreaming(false);

        // 2. commit final assistant message
        addLocalMessage({
          id: `assistant-${Date.now()}`,
          role: "assistant",
          message: accumulatedText,
          charts: accumulatedCharts,
          static_charts: accumulatedStaticCharts,
          isStreaming: false,
        });

        // 3. analytics
        const responseType =
          accumulatedCharts.length || accumulatedStaticCharts.length
            ? "chart"
            : "text";

        let isFallback: "yes" | "no" = "no";
        try {
          const parsedTail = JSON.parse(accumulatedText);
          if (parsedTail?.valid_question === false) {
            isFallback = "yes";
          }
        } catch {
          // normal response
        }

        gaService.conversationAIResponse(
          responseType,
          isFallback,
          sessionId ?? headerSid ?? "unknown"
        );
      } else {
        // no accumulated text, still ensure streaming flags are reset
        setStreamingMsg(null);
        setIsStreaming(false);
      }

      // fire-and-forget sessions reload for first chat
      if (isNewChat.current) {
        loadSessions();
      }

      setIsLoading(false);
    } catch (err) {
      setIsStreaming(false);
      setIsLoading(false);

      addLocalMessage({
        id: `assistant-${Date.now()}`,
        role: "assistant",
        message:
          "\n\n[Error] Something went wrong while streaming. Please try again.",
        charts: [],
        static_charts: [],
      });
      setStreamingMsg(null);
    } finally {
      abortRef.current = null;
    }
  }

  // --- Keyboard handling ---
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
      e.preventDefault();
      setInputValue("");
      return;
    }
    if (e.key === "Escape") {
      (e.target as HTMLTextAreaElement).blur();
      return;
    }
    if (
      e.key === "ArrowUp" &&
      (e.currentTarget.selectionStart ?? 0) === 0 &&
      (e.currentTarget.selectionEnd ?? 0) === 0 &&
      !inputValue
    ) {
      e.preventDefault();
      if (lastSent) setInputValue(lastSent);
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (inputValue.trim()) {
        handleSendMessage(inputValue);
      }
      return;
    }
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      if (inputValue.trim()) {
        handleSendMessage(inputValue);
      }
    }
  };

  const canSend = !!inputValue.trim() && !isLoading;

  // 🔔 Listen for conversational:setInput and highlight send
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ msg?: string }>;
      const msg = ce?.detail?.msg;
      if (typeof msg === "string") {
        setInputValue(msg);
        setTimeout(() => textAreaRef.current?.focus(), 0);

        setSendHighlighted(true);
        if (highlightTimeoutRef.current) {
          window.clearTimeout(highlightTimeoutRef.current);
        }
        highlightTimeoutRef.current = window.setTimeout(() => {
          setSendHighlighted(false);
        }, 5000);
      }
    };

    window.addEventListener(
      "conversational:setInput",
      handler as EventListener
    );

    return () => {
      window.removeEventListener(
        "conversational:setInput",
        handler as EventListener
      );
      if (highlightTimeoutRef.current) {
        window.clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  const glowingClass = "glowing-border";
  const isMobile = useDevice();

  return (
    <Suspense>
    <Box
      sx={{
        px: { xs: 2, sm: 3, md: 6 },
        py: { xs: 2, md: 3 },
        width: "100%",
      }}
    >
      <Box
        className={glowingClass}
        sx={
          {
            position: "relative",
            width: "100%",

            "--surface": "#151627",
            "--border-width": "1px",
            "--glow-size": "22px",
            "--glow-intensity": isLoading ? 0.4 : 0,
            "--glow-scale": isLoading ? 1 : 0,
            "--clr-1": "#6540f7",
            "--clr-2": "#8b5cf6",
            "--clr-3": "#38bdf8",
            "--clr-4": "#22c55e",
            "--clr-5": "#0ea5e9",
            animation: isLoading ? "glow 2s linear infinite" : "none",
          } as any
        }
      >
        <Box
          sx={{
            height: "100%",
            widht: "100%",
            background: "#101015",
            borderRadius: 5,
            p: 2,
          }}
        >
          <textarea
            ref={textAreaRef}
            rows={1}
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: "16px",
              fontSize: "16px",
              outline: "none",
              border: "none",
              backgroundColor: "transparent",
              color: "white",
              boxSizing: "border-box",
              resize: "none",
              lineHeight: "1.5",
              maxHeight: 160,
              overflowY: "auto",
              position: "relative",
              zIndex: 1,
            }}
            placeholder={
              isLoading
                ? isMobile
                  ? "Receiving answer…"
                  : "Receiving answer… (Esc to unfocus, Stop to cancel)"
                : isMobile
                ? "Type your question…"
                : "Type your question… (Enter to send, Shift+Enter for newline)"
            }
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={onKeyDown}
            aria-label="Chat input"
            disabled={isLoading}
          />

          <Stack
            direction="row"
            justifyContent="flex-end"
            sx={{ mt: 1, px: 1, position: "relative", zIndex: 1 }}
            gap={2}
          >
            {/* Voice */}
            <Tooltip
              title={
                isLoading
                  ? "Voice input is disabled while receiving an answer"
                  : "Ask with voice"
              }
              placement="top"
              arrow
            >
              <span>
                <button
                  style={{
                    color: "white",
                    border: "none",
                    background: "#ffffff20",
                    padding: "10px",
                    cursor: isLoading ? "not-allowed" : "pointer",
                    borderRadius: "50%",
                    opacity: isLoading ? 0.5 : 1,
                  }}
                  onClick={() => !isLoading && setVoiceOpen(true)}
                  aria-label="Open voice input"
                  disabled={isLoading}
                >
                  <Mic size={18} />
                </button>
              </span>
            </Tooltip>

            {/* Stop streaming */}
            {isLoading ? (
              <Tooltip title="Stop generating" placement="top" arrow>
                <span>
                  <button
                    style={{
                      color: "white",
                      border: "none",
                      background: "#ff525240",
                      padding: "10px",
                      cursor: "pointer",
                      borderRadius: "50%",
                    }}
                    onClick={stopStreaming}
                    aria-label="Stop generating"
                  >
                    <Square size={18} />
                  </button>
                </span>
              </Tooltip>
            ) : null}

            {/* Send with glow wrapper */}
            <Box
              sx={{
                borderRadius: "50%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                ...(sendHighlighted && {
                  animation: `${glowPulse} 1.1s ease-out infinite`,
                }),
              }}
            >
              <Tooltip
                title={
                  canSend
                    ? "Send (Enter). Use Shift+Enter for new line."
                    : "Type a message to send"
                }
                placement="top"
                arrow
              >
                <span>
                  <button
                    style={{
                      color: "white",
                      border: "none",
                      padding: "10px",
                      cursor: canSend ? "pointer" : "not-allowed",
                      fontSize: "16px",
                      background: "transparent",
                      borderRadius: "50%",
                      opacity: canSend ? 1 : 0.5,
                    }}
                    onClick={() => canSend && handleSendMessage(inputValue)}
                    aria-label="Send message"
                    disabled={!canSend}
                  >
                    <SendHorizontal size={18} />
                  </button>
                </span>
              </Tooltip>
            </Box>
          </Stack>

          {voiceOpen && (
            <VoiceProvider>
              <FullScreenSpeechInput
                onSend={(query: string) => {
                  setInputValue(query);
                  setVoiceOpen(false);
                  const trimmed = query.trim();
                  if (trimmed && !isLoading) handleSendMessage(trimmed);
                }}
                onClose={() => setVoiceOpen(false)}
                setQuery={setInputValue}
              />
            </VoiceProvider>
          )}
        </Box>
      </Box>
    </Box>
    </Suspense>
  );
};

export default ConversationalInputBox;
